import { useState, type FormEvent } from "react";
import { describeError } from "@/api/client";
import { Button, Dialog, DialogClose, InlineAlert, RadioGroup, TextField } from "@/components";
import { useCreateFormula, type Formula, type FormulaKind } from "../api";
import styles from "./FormulaDialogs.module.css";

const KINDS: { value: FormulaKind; label: string; description: string }[] = [
  { value: "formula", label: "Formula", description: "A perfume or trial: F-0001" },
  { value: "accord", label: "Accord", description: "A building block you nest in formulas: A-0001" },
  { value: "dilution", label: "Dilution", description: "A working dilution of one material: D-0001" },
];

export interface NewFormulaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (formula: Formula) => void;
}

/** Name, kind and tags for a new formula; it opens with an empty draft v1. */
export function NewFormulaDialog({ open, onOpenChange, onCreated }: NewFormulaDialogProps) {
  const create = useCreateFormula();
  const [name, setName] = useState("");
  const [kind, setKind] = useState<FormulaKind>("formula");
  const [tags, setTags] = useState("");
  const [family, setFamily] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);

  function reset() {
    setName("");
    setKind("formula");
    setTags("");
    setFamily("");
    setNameError(null);
    create.reset();
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!name.trim()) {
      setNameError("Give the formula a name");
      return;
    }
    create.mutate(
      {
        name: name.trim(),
        kind,
        family: family.trim(),
        tags: tags
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
      },
      {
        onSuccess: (formula) => {
          onOpenChange(false);
          reset();
          onCreated(formula);
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) reset();
      }}
      title="New formula"
      description="It starts as an empty draft v1. Add lines in %, parts or grams."
      footer={
        <>
          <DialogClose />
          <Button
            variant="primary"
            type="submit"
            form="new-formula"
            pending={create.isPending}
            pendingLabel="Creating…"
          >
            Create formula
          </Button>
        </>
      }
    >
      <form id="new-formula" className={styles.form} onSubmit={submit} noValidate>
        <TextField
          label="Name"
          name="name"
          autoComplete="off"
          value={name}
          onValueChange={(value) => {
            setName(value);
            setNameError(null);
          }}
          error={nameError}
          required
        />
        <RadioGroup label="Kind" options={KINDS} value={kind} onValueChange={setKind} />
        <div className={styles.row}>
          <TextField
            label="Family"
            name="family"
            optional
            value={family}
            onValueChange={setFamily}
            placeholder="Chypre"
          />
          <TextField
            label="Tags"
            name="tags"
            optional
            value={tags}
            onValueChange={setTags}
            placeholder="marine, summer"
            description="Separate tags with commas"
          />
        </div>
        {create.error ? (
          <InlineAlert tone="danger" title="Couldn’t create the formula">
            {describeError(create.error)}
          </InlineAlert>
        ) : null}
      </form>
    </Dialog>
  );
}
